import { LANGUAGES, type LangCode } from "@1sky/shared";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

type Language = (typeof LANGUAGES)[number];

const fold = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase();

/**
 * Languages matching what the user typed, best first: exact code, then names (English or native,
 * ignoring accents) that start with it, then names with a word that does, then names containing it.
 */
export function searchLanguages(query: string): Language[] {
  const q = fold(query.trim());
  if (!q) return [...LANGUAGES];
  const hits: { l: Language; score: number }[] = [];
  for (const l of LANGUAGES) {
    const names = [fold(l.label), fold(l.native)];
    const score =
      l.code === q ? 0
      : names.some((n) => n.startsWith(q)) ? 1
      : names.some((n) => n.split(/[\s()-]+/).some((w) => w.startsWith(q))) ? 2
      : names.some((n) => n.includes(q)) ? 3
      : -1;
    if (score >= 0) hits.push({ l, score });
  }
  return hits.sort((a, b) => a.score - b.score).map((h) => h.l); // stable: keeps list order within a score
}

/** Searchable language picker (combobox): type an English or native name, or a code. */
export function LanguageSelect({
  value,
  onChange,
  label = "Your native language",
  hint,
  className = "",
}: {
  value: LangCode;
  onChange: (l: LangCode) => void;
  label?: string;
  hint?: ReactNode;
  className?: string;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const [query, setQuery] = useState<string | null>(null); // null = not typing, show the selection
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const list = useRef<HTMLUListElement>(null);
  const selected = LANGUAGES.find((l) => l.code === value);
  const results = useMemo(() => searchLanguages(query ?? ""), [query]);
  const optionId = (l: Language) => `${id}-opt-${l.code}`;

  useEffect(() => {
    if (open) list.current?.children[active]?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);

  function show() {
    setOpen(true);
    setActive(Math.max(0, results.findIndex((l) => l.code === value)));
  }
  function close() {
    setQuery(null);
    setOpen(false);
  }
  function choose(l: Language) {
    onChange(l.code);
    close();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) return show();
      setActive((a) => Math.min(results.length - 1, Math.max(0, a + (e.key === "ArrowDown" ? 1 : -1))));
    } else if (e.key === "Enter" && open && results[active]) {
      e.preventDefault();
      choose(results[active]);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      close();
    }
  }

  return (
    <div className={`relative rounded-xl border border-line bg-surface px-4 py-2.5 focus-within:border-ink ${className}`}>
      <label htmlFor={id} className="block cursor-text text-xs font-semibold text-ink">
        {label}
      </label>
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && results[active] ? optionId(results[active]) : undefined}
        autoComplete="off"
        spellCheck={false}
        value={query ?? (selected ? `${selected.flag} ${selected.native}` : value)}
        placeholder="Type to search languages"
        onFocus={(e) => {
          e.target.select();
          show();
        }}
        onBlur={close}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
        className="field-control emoji-text w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-muted"
      />
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
      {open && (
        <ul ref={list} id={listId} role="listbox" aria-label="Languages" className="absolute inset-x-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-xl border border-line bg-surface py-1 shadow-card">
          {results.length ? (
            results.map((l, i) => (
              <li
                key={l.code}
                id={optionId(l)}
                role="option"
                aria-selected={l.code === value}
                onMouseDown={(e) => e.preventDefault()} // keep focus in the input
                onClick={() => choose(l)}
                onMouseEnter={() => setActive(i)}
                className={`flex cursor-pointer items-center gap-2 px-4 py-2 text-[15px] ${i === active ? "bg-soft" : ""}`}
              >
                <span className="emoji-text">{l.flag}</span>
                <span className="font-medium">{l.native}</span>
                {l.native !== l.label && <span className="text-muted">{l.label}</span>}
                {l.code === value && (
                  <span className="ml-auto text-accent" aria-hidden>
                    ✓
                  </span>
                )}
              </li>
            ))
          ) : (
            <li role="presentation" className="px-4 py-2 text-sm text-muted">
              No language matches “{query}”
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
